import type Handsontable from 'handsontable';
import {
  cellResetsToAs400,
  cellResetsToSaved,
  selectedCells,
  writableCellAt,
  type CellResetsFor,
} from './as400GridSelection';

type MenuItem = Handsontable.plugins.ContextMenu.MenuItemConfig;

// The change source a reset writes under: not typed, so it skips the caps and
// ASCII rewrites — the value going back is one the grid already held.
export const RESET_SOURCE = 'As400Grid.reset';

/** The cells of a live grid's selection that `resetsFor` would change. */
function selectionResets(instance: Handsontable, resetsFor: CellResetsFor) {
  return resetsFor(selectedCells(instance), writableCellAt(instance));
}

/**
 * A reset item over the selection: `resetsFor` names the cells that change
 * and their values. When nothing in the selection would change, it is
 * `whenIdle`: greyed out, or not shown at all.
 */
function resetItem(
  menuLabel: string,
  resetsFor: CellResetsFor,
  whenIdle: 'disabled' | 'hidden',
): MenuItem {
  const idle = (instance: Handsontable) =>
    selectionResets(instance, resetsFor).length === 0;
  return {
    name: menuLabel,
    [whenIdle](this: Handsontable) {
      return idle(this);
    },
    callback(this: Handsontable) {
      const resets = selectionResets(this, resetsFor);
      if (resets.length > 0) this.setDataAtRowProp(resets, RESET_SOURCE);
    },
  };
}

/**
 * "Reset to last saved value": drop the selection's unsaved edits. Shown only
 * while the selection has one.
 */
export function resetToSavedItem(menuLabel: string): MenuItem {
  return resetItem(menuLabel, cellResetsToSaved, 'hidden');
}

/**
 * "Reset to AS/400 value": put the selection back to what the AS/400 has,
 * unsaved and saved edits alike. A saved edit's reset is a pending edit that
 * takes effect on Save. Always shown, greyed out when there is nothing to
 * reset, so the menu always has it to find.
 */
export function resetToAs400Item(menuLabel: string): MenuItem {
  return resetItem(menuLabel, cellResetsToAs400, 'disabled');
}
